import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { InstallationItemGatewayDevice, ViError } from '../../../../../../projects/client';
import { InstallationItemGatewayDeviceFull } from '../../../../../../projects/client/apis/bff-installations/bff-installations.model';
import { ViIconComponent } from '../../../../../../projects/ui';
import { ViInputStandaloneComponent } from '../../../../../../projects/ui/components/input/input-standalone.component';
import { DevicesStore } from '../../../../../@store';
import {
  SearchDeviceItemViewValue,
  ServiceTicketBoilerSerialErrors,
  ServiceTicketStore,
} from '../../../../../@store/service-ticket';
import { CustomTranslationsService } from '../../../../../operational-data-common/translations';
import { CommonStandaloneModule } from '../../../../common-standalone.module';
import { CodeScannerService, GTMService } from '../../../../services';
import { CodeScannerResponseType } from '../../../../services/code-scanner.model';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'app-service-ticket-boiler-serial-field',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    ViInputStandaloneComponent,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    ViIconComponent,
  ],
  templateUrl: './service-ticket-boiler-serial-field.component.html',
  styleUrls: ['./service-ticket-boiler-serial-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceTicketBoilerSerialFieldComponent implements OnInit, OnDestroy {
  CodeScannerResponseType = CodeScannerResponseType;

  boilerSerialAutoCompleteValue: { value: string; viewValue: string };
  devicesFromSearchResult: SearchDeviceItemViewValue[] = [];

  valueChanged$ = new Subject<string>();

  serialValidBypassValue = false;

  isCheckboxDisabled: boolean;
  errorMessage: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private isBoilerSerialChecked = false;

  constructor(
    public codeScannerService: CodeScannerService,
    public serviceTicketStore: ServiceTicketStore,
    public gtmService: GTMService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private customTranslationsService: CustomTranslationsService,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    const installationId = this.checkIfInstallationIdIsPresent();

    combineLatest([
      this.devicesStore.mainDeviceBoilerSerial$.pipe(take(1)),
      this.serviceTicketStore.boilerSerialAutocompleteDevices(installationId),
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([serial, devices]) => {
        if (!this.isBoilerSerialChecked && devices.length === 1) {
          this.serviceTicketStore.updateSearchedBoilerSerial(serial);
          this.boilerSerialAutoCompleteValue = { value: serial, viewValue: serial };
        }
        this.isBoilerSerialChecked = true;
        this.devicesFromSearchResult = devices.map((entry) => {
          return this.mapToViewValue(entry);
        });
        this.cdr.markForCheck();
      });

    this.valueChanged$
      .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe$))
      .subscribe((value) => {
        this.onInputValueChange(value);
      });

    this.serviceTicketStore.boilerSerialValidationError$
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((error: ViError | ServiceTicketBoilerSerialErrors) => {
        this.isCheckboxDisabled = false;
        this.errorMessage = '';
        if (error) {
          if (typeof error === 'string') {
            this.boilerValidationErrorsMapper(error);
          } else {
            this.errorMessage = this.translateService.instant(
              '+SERVICE_TOOL.BOILER_SERIAL_FIELD_INVALID'
            );
          }
        }
      });
    this.cdr.markForCheck();
  }

  openScanner(): void {
    this.codeScannerService
      .openCodeDialog()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((response) => {
        if (response.type === CodeScannerResponseType.CODE_SCANNER_RESULT) {
          this.boilerSerialAutoCompleteValue = { value: response.value, viewValue: response.value };
          this.valueChanged$.next(response.value);
        }
      });
  }

  private mapToViewValue(device: InstallationItemGatewayDeviceFull): SearchDeviceItemViewValue {
    const viewValue = `${device.marketingName} (${device.serial}) - ${device.ownerName}`;

    const item = {
      value: device,
      viewValue,
      inputViewValue: device.serial,
      column: [`${device.serial}`, `${device.marketingName}`],
    };

    if (
      device.deviceType &&
      !!this.customTranslationsService.CASCADE_DEVICE_TYPES[device.deviceType]
    ) {
      item.column.push(
        `${this.translateService.instant(
          this.customTranslationsService.CASCADE_DEVICE_TYPES[device.deviceType]
        )}`
      );
    }

    return item;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  onInputValueChange(inputValue: string): void {
    if (!inputValue) {
      this.devicesFromSearchResult = [];
    }
    this.serialValidBypassValue = false;

    const deviceDetails = this.getDeviceDetails({ boilerSerial: inputValue.trim() });
    const serial = deviceDetails ? deviceDetails.serial : inputValue.trim();

    this.serviceTicketStore.updateSearchedBoilerSerial(serial);
  }

  private getDeviceDetails(query: {
    boilerSerial?: string;
    installationId?: number;
  }): InstallationItemGatewayDevice {
    const foundDeviceEntry = this.devicesFromSearchResult?.find((entry) => {
      const foundBoilerSerial = entry?.value?.serial === query.boilerSerial;
      const foundInstallationId =
        entry?.value && entry?.value['installationId'] === query.installationId;

      if (foundBoilerSerial || foundInstallationId) {
        return true;
      }
    });

    return foundDeviceEntry?.value;
  }

  onSerialValidBypassChange(checkboxValue: boolean): void {
    this.serialValidBypassValue = checkboxValue;
    this.serviceTicketStore.bypassBoilerSerialValidation();
  }

  // Cannot use Angular's Router because this component is outside <app-root>
  private checkIfInstallationIdIsPresent(): string {
    const url = window.location.href;
    const regex = /\/installations\/(\d+)/;
    const match = url.match(regex);

    if (match && match[1]) {
      const installationId = match[1];

      return installationId;
    }
  }

  private boilerValidationErrorsMapper(string: ServiceTicketBoilerSerialErrors): void {
    this.isCheckboxDisabled = true;

    switch (string) {
      case ServiceTicketBoilerSerialErrors.NonNumericValue:
        this.errorMessage = this.translateService.instant(
          '+SERVICE_TOOL.BOILER_SERIAL_FIELD_INVALID_NUMERIC_VALUES'
        );
        break;
      case ServiceTicketBoilerSerialErrors.GatewaySerialInsteadOfBoilerSerial:
        this.errorMessage = this.translateService.instant(
          '+SERVICE_TOOL.GATEWAY_SERIAL_INSTEAD_OF_BOILER_SERIAL'
        );
        break;
    }
  }
}
